import React from 'react';
import { ArrowRight } from 'lucide-react';
import { RELEASES } from '@/config/site';
import { ExtLink } from './Social';
import { ApiRelease } from '@/hooks/useSiteContent';
import { mediaUrl } from '@/lib/api';
import { usePlayerModal, canOpenRelease } from './EmbedModal';

/** desktop slot geometry measured from the reference (5 slots) */
const SLOTS = RELEASES.map((r) => ({ x: r.x, w: r.w }));

export function ReleaseMeta({ r }: { r: ApiRelease }) {
  return (
    <p className="ek-rel-meta">
      {(r.type || '').toUpperCase()}
      {r.type && r.year && <i aria-hidden="true" />}
      {r.year}
    </p>
  );
}

export function useOpenRelease() {
  const { openRelease } = usePlayerModal();
  return openRelease;
}

export default function LatestReleases({ releases, allReleasesUrl }: { releases: ApiRelease[]; allReleasesUrl: string }) {
  const openRelease = useOpenRelease();
  const { openAll } = usePlayerModal();
  return (
    <section id="releases" className="ek-releases" aria-labelledby="releases-title" data-testid="releases-section">
      <h2 id="releases-title" className="ek-sec-title ek-rel-title">LATEST RELEASES</h2>
      <ExtLink
        url={allReleasesUrl}
        label="View all releases"
        className="ek-sec-link ek-rel-link"
        testId="view-all-releases-link"
        onFallback={openAll}
      >
        VIEW ALL RELEASES <ArrowRight size={12} strokeWidth={1.8} />
      </ExtLink>

      <div className="ek-rel-grid">
        {releases.slice(0, 5).map((r, i) => (
          <article
            key={r.id}
            className="ek-rel-card"
            style={{ ['--x' as string]: `${SLOTS[i].x}px`, ['--w' as string]: `${SLOTS[i].w}px` } as React.CSSProperties}
            data-testid={`release-card-${r.slug || r.id}`}
          >
            <ExtLink
              url={r.spotify_url}
              label={r.title}
              className="ek-rel-cover"
              testId={`release-link-${r.slug || r.id}`}
              onOpen={() => openRelease(r)}
            >
              {r.cover_url ? <img src={mediaUrl(r.cover_url)} alt={`${r.title} — cover art`} draggable={false} /> : <span className="ek-cover-empty" />}
              {canOpenRelease(r) && (
                <span className="ek-rel-play" aria-hidden="true">
                  <svg viewBox="0 0 12 13" width="14" height="15">
                    <path d="M1.6 1.2c0-.7.8-1.1 1.4-.7l8.2 5.2c.5.3.5 1.1 0 1.5L3 12.4c-.6.4-1.4 0-1.4-.7V1.2z" fill="currentColor" />
                  </svg>
                </span>
              )}
            </ExtLink>
            <h3 className="ek-rel-name">{r.title}</h3>
            <p className="ek-rel-artist">{r.artist}</p>
            <ReleaseMeta r={r} />
          </article>
        ))}
      </div>
    </section>
  );
}
